import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  MiCheckbox,
  MiContentDrawer,
  MiDetailFields,
} from "@miplatform/components";
import {
  ACCOUNT_TYPE,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  VENDOR_TYPE_OPTIONS,
  regionCodesUS,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAccount, mdiAccountHardHat, mdiCurrencyUsd, mdiMap } from "@mdi/js";
import { Box } from "@mui/material";
import {
  ACCOUNTS_TAB_TO_TYPE,
  ACCOUNT_PAGE_TYPE,
  CUSTOMER_OPTIONS,
  VENDOR_OPTIONS,
} from "../accountsConstants";
import { useAddAccountViewModel } from "./useAddAccountViewModel";

const createSchema = (accountType) => {
  const baseSchema = {
    name: z
      .string()
      .min(1, "Builder name is required")
      .regex(/^[^:"]*$/, "Name cannot contain colons or double quotes"),
    code: z
      .string()
      .min(1, "Abbreviation is required")
      .max(6, "Abbreviation must be 6 characters or less"),
    accountType: z.string().min(1, "Account type is required"),
    address: z.object({
      addressLine1: z
        .string()
        .min(1, "Address is required")
        .regex(
          /^[^:"]*$/,
          "Address line 1 cannot contain colons or double quotes"
        ),
      addressLine2: z
        .string()
        .regex(
          /^[^:"]*$/,
          "Address line 2 cannot contain colons or double quotes"
        )
        .optional(),
      city: z.string().min(1, "City is required"),
      regionCode: z.string().min(1, "State is required"),
      countryCode: z
        .string()
        .max(3, "Country code must be 3 characters or less")
        .optional(),
      postalCode: z
        .string()
        .min(5, "Zip Code is required")
        .max(5, "Zip Code must be 5 digits"),
    }),
    billingAddress: z.object({
      addressLine1: z
        .string()
        .min(1, "Address is required")
        .regex(
          /^[^:"]*$/,
          "Address line 1 cannot contain colons or double quotes"
        ),
      addressLine2: z
        .string()
        .regex(
          /^[^:"]*$/,
          "Address line 2 cannot contain colons or double quotes"
        )
        .optional(),
      city: z.string().min(1, "City is required"),
      regionCode: z.string().min(1, "State is required"),
      countryCode: z.string().optional(),
      postalCode: z
        .string()
        .min(5, "Zip Code is required")
        .max(5, "Zip Code must be 5 digits"),
    }),
    defaultRetainagePercentage: z.preprocess(
      (val) => (val === "" || val === null || val === undefined ? null : val),
      z.coerce
        .number()
        .min(0.01, "Retainage percentage must be greater than 0%")
        .max(100, "Retainage percentage cannot exceed 100%")
        .nullable()
    ),
    enableRetainage: z.boolean().optional(),
    enableDiscount: z.boolean().optional(),
    defaultDiscountPercentage: z.preprocess(
      (val) => (val === "" || val === null || val === undefined ? null : val),
      z.coerce
        .number()
        .min(0.01, "Discount percentage must be greater than 0%")
        .max(100, "Discount percentage cannot exceed 100%")
        .nullable()
    ),
    warrantyPeriodInMonths: z.number().optional().nullable(),
    paymentTerms: z
      .string()
      .max(50, "Payment terms must be less than 50 characters")
      .optional(),
    quickBooksId: z.string().optional(),
  };

  const schemaObj = {
    ...baseSchema,
    supplierType:
      accountType === ACCOUNT_PAGE_TYPE.VENDOR
        ? z.string().min(1, "Supplier type is required")
        : z.string().nullable().optional(),
  };

  const finalSchema = z.object(schemaObj).superRefine((data, ctx) => {
    if (
      data.enableRetainage &&
      (data.defaultRetainagePercentage === null ||
        data.defaultRetainagePercentage === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Retainage percentage is required when retainage is enabled",
        path: ["defaultRetainagePercentage"],
      });
    }

    if (
      data.enableDiscount &&
      (data.defaultDiscountPercentage === null ||
        data.defaultDiscountPercentage === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Discount percentage is required when discount is enabled",
        path: ["defaultDiscountPercentage"],
      });
    }
  });

  return finalSchema;
};

export const AddAccount = ({
  type,
  tab,
  onCloseDrawer,
  onRefresh,
  showDrawer,
  initialData = null,
  isAccountTypeEditable = false,
}) => {
  const schema = createSchema(type);

  const {
    formState: { isValid },
    control,
    reset,
    setValue,
    getValues,
    watch,
    handleSubmit,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: initialData?.name || "",
      code: initialData?.code || "",
      accountType: initialData?.accountType || ACCOUNTS_TAB_TO_TYPE[tab],
      warrantyPeriodInMonths: null,
      enableRetainage: false,
      defaultRetainagePercentage: null,
      enableDiscount: false,
      defaultDiscountPercentage: null,
      address: {
        addressLine1: initialData?.address?.addressLine1 || "",
        addressLine2: initialData?.address?.addressLine2 || "",
        city: initialData?.address?.city || "",
        regionCode: initialData?.address?.regionCode || "",
        postalCode: initialData?.address?.postalCode || "",
        countryCode: initialData?.address?.countryCode || "",
      },
      billingAddress: {
        addressLine1: initialData?.billingAddress?.addressLine1 || "",
        addressLine2: initialData?.billingAddress?.addressLine2 || "",
        city: initialData?.billingAddress?.city || "",
        regionCode: initialData?.billingAddress?.regionCode || "",
        postalCode: initialData?.billingAddress?.postalCode || "",
        countryCode: initialData?.billingAddress?.countryCode || "",
      },
      supplierType: initialData?.supplierType,
      paymentTerms: "",
      quickBooksId: initialData?.quickBooksId || undefined,
    },
    resolver: zodResolver(schema),
  });
  const { isLoading, handleCreateCustomer } = useAddAccountViewModel({
    onCloseDrawer,
    onRefresh,
    reset,
  });

  const [isBillingAddressSame, setIsBillingAddressSame] = useState(false);

  const enableRetainage = watch("enableRetainage");
  const enableDiscount = watch("enableDiscount");

  const watchedAddress = watch([
    "address.addressLine1",
    "address.addressLine2",
    "address.city",
    "address.regionCode",
    "address.countryCode",
    "address.postalCode",
  ]);

  useEffect(() => {
    if (isBillingAddressSame) {
      const currentAddress = getValues("address");
      setValue("billingAddress", currentAddress);
    }
  }, [watchedAddress, isBillingAddressSame]);

  const basicInfoFieldset = [
    {
      formControl: control,
      label: "Account Type",
      name: "accountType",
      width: "100%",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: [...CUSTOMER_OPTIONS, ...VENDOR_OPTIONS],
      readOnly: !isAccountTypeEditable,
    },
    {
      formControl: control,
      label: "Customer Name",
      name: "name",
      width: "50%",
      required: true,
    },
    {
      formControl: control,
      label: "Abbreviation",
      name: "code",
      width: "50%",
      required: true,

      inputProps: { maxLength: 6 },
    },
    {
      formControl: control,
      name: "paymentTerms",
      label: "Invoice Payment Terms",
      width: "50%",
    },
    {
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      allowNegative: false,
      name: "warrantyPeriodInMonths",
      label: "Warranty Period (years)",
      width: "50%",
      style: {
        display:
          ACCOUNTS_TAB_TO_TYPE[tab] === ACCOUNT_TYPE.BUILDER ? "block" : "none",
      },
      value: getValues("warrantyPeriodInMonths")
        ? getValues("warrantyPeriodInMonths") / 12
        : null,
      setValue: (value) => {
        setValue("warrantyPeriodInMonths", value ? value * 12 : null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      formControl: control,
      label: "Supplier Type",
      name: "supplierType",
      width: "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: VENDOR_TYPE_OPTIONS,
      required: type === ACCOUNT_PAGE_TYPE.VENDOR,
      defaultValue: "Other",
      style: {
        display: type === ACCOUNT_PAGE_TYPE.VENDOR ? "block" : "none",
      },
    },
  ];

  const financialSettingsFieldset = [
    {
      formControl: control,
      name: "enableRetainage",
      label: "Enable Default Retainage",
      width: "100%",
      fieldType: INPUT_TYPES.CHECKBOX,
      setValue: (event) => {
        setValue("enableRetainage", event.target.checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (!event.target.checked) {
          setValue("defaultRetainagePercentage", null, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      },
    },
    ...(enableRetainage
      ? [
          {
            formControl: control,
            name: "defaultRetainagePercentage",
            label: "Retainage Percentage",
            width: "50%",
            required: true,
            fixedDecimalScale: 2,
            allowNegative: false,
            fieldType: INPUT_TYPES.NUMERIC,
            suffix: "%",
            style: {
              minHeight: "80px",
            },
            setValue: (value) => {
              setValue("defaultRetainagePercentage", value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
          },
        ]
      : []),
    {
      formControl: control,
      name: "enableDiscount",
      label: "Enable Default Discount",
      width: "100%",
      fieldType: INPUT_TYPES.CHECKBOX,
      setValue: (event) => {
        setValue("enableDiscount", event.target.checked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (!event.target.checked) {
          setValue("defaultDiscountPercentage", null, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      },
    },
    ...(enableDiscount
      ? [
          {
            formControl: control,
            name: "defaultDiscountPercentage",
            label: "Discount Percentage",
            width: "50%",
            required: true,
            fixedDecimalScale: 2,
            allowNegative: false,
            fieldType: INPUT_TYPES.NUMERIC,
            suffix: "%",
            style: {
              minHeight: "80px",
            },
            setValue: (value) => {
              setValue("defaultDiscountPercentage", value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
          },
        ]
      : []),
  ];
  const addressInfoFieldset = [
    {
      formControl: control,
      label: "Address Line 1",
      name: "address.addressLine1",
      width: "50%",
      required: true,
    },
    {
      formControl: control,
      label: "Address Line 2",
      name: "address.addressLine2",
      width: "50%",
    },

    {
      formControl: control,
      label: "City",
      name: "address.city",
      width: "50%",
      required: true,
    },
    {
      formControl: control,
      label: "State",
      name: "address.regionCode",
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      width: "50%",
      required: true,
    },
    {
      formControl: control,
      label: "Country",
      name: "address.countryCode",
      width: "50%",
      inputProps: { maxLength: 3 },
    },
    {
      formControl: control,
      label: "Zip",
      name: "address.postalCode",
      width: "50%",
      required: true,
      type: "number",
    },
  ];
  const billingAddressInfoFieldset = [
    {
      formControl: control,
      label: "Address Line 1",
      name: "billingAddress.addressLine1",
      width: "50%",
      required: true,
      disabled: isBillingAddressSame,
    },
    {
      formControl: control,
      label: "Address Line 2",
      name: "billingAddress.addressLine2",
      width: "50%",
      disabled: isBillingAddressSame,
    },
    {
      formControl: control,
      label: "City",
      name: "billingAddress.city",
      width: "50%",
      required: true,
      disabled: isBillingAddressSame,
    },
    {
      formControl: control,
      label: "State",
      name: "billingAddress.regionCode",
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      width: "50%",
      required: true,
      disabled: isBillingAddressSame,
    },
    {
      formControl: control,
      label: "Country",
      name: "billingAddress.countryCode",
      width: "50%",
      inputProps: { maxLength: 3 },
      disabled: isBillingAddressSame,
    },
    {
      formControl: control,
      label: "Zip",
      name: "billingAddress.postalCode",
      width: "50%",
      required: true,
      type: "number",
      disabled: isBillingAddressSame,
    },
  ];

  const handleCancel = () => {
    onCloseDrawer();
    reset();
  };

  return (
    <MiContentDrawer
      open={showDrawer}
      title={`Add ${ACCOUNTS_TAB_TO_TYPE[tab] ?? "New Customer"}`}
      leftIcon={mdiAccountHardHat}
      variant="temporary"
      onClose={handleCancel}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleCancel,
        },
        {
          name: "Save",
          variant: BUTTON_VARIANTS.CONTAINED,
          disabled: !isValid || isLoading,
          onClick: handleSubmit((values) => handleCreateCustomer(values)),
        },
      ]}
    >
      <Box p={4}>
        <MiDetailFields
          headerProps={{
            icon: mdiAccount,
            title: "Basic Info",
          }}
          detailFields={basicInfoFieldset}
        />
        <MiDetailFields
          headerProps={{
            icon: mdiCurrencyUsd,
            title: "Financial Settings",
          }}
          detailFields={financialSettingsFieldset}
        />
        <MiDetailFields
          headerProps={{
            icon: mdiMap,
            title: "Address Info",
          }}
          detailFields={addressInfoFieldset}
        />
        <MiCheckbox
          label="Billing address is same as primary address"
          value={isBillingAddressSame}
          style={{ padding: "16px 4px" }}
          handleChange={(e) => {
            if (e.target.checked) {
              setIsBillingAddressSame(true);
              setValue("billingAddress", getValues("address"), {
                shouldValidate: true,
              });
            } else {
              setIsBillingAddressSame(false);
              setValue(
                "billingAddress",
                {
                  addressLine1: "",
                  addressLine2: "",
                  city: "",
                  regionCode: "",
                  postalCode: "",
                  countryCode: "",
                },
                { shouldValidate: true }
              );
            }
          }}
        />
        <MiDetailFields
          headerProps={{
            icon: mdiMap,
            title: "Billing Address Info",
          }}
          detailFields={billingAddressInfoFieldset}
        />
      </Box>
    </MiContentDrawer>
  );
};
